import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "./firebase";
import { doc, setDoc } from "firebase/firestore";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";
import GoogleSignInButton from "./GoogleSignInButton";
import {
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import {
  Eye,
  EyeOff,
  ShieldCheck,
  ChevronLeft,
  Globe,
  Lock,
} from "lucide-react";

const RegisterForm = () => {
  const navigate = useNavigate();
  const recaptchaRef = useRef();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
   const [showDetailsPopup, setShowDetailsPopup] = useState(false);
    const [tempUser, setTempUser] = useState(null);
      const [countries, setCountries] = useState([]);
      const [additionalData, setAdditionalData] = useState({ country: null, domainName: "" });
  
  // State for the initial page load spinner
  const [isPageReady, setIsPageReady] = useState(false);
const [showSpinner, setShowSpinner] = useState(false);

  // --- Theme Colors ---
  const primaryOrange = "#FF6B00";
  const darkOrange = "#E66000";
  const borderColor = "#E5E7EB";

  // --- Logic to check if form is valid ---
  const isFormInvalid = 
    name.length < 3 || 
    !email || 
    !country || 
    !password || 
    !agree || 
    loading;

  // --- Responsive Logic & Initial Load Delay ---
useEffect(() => {
  const handleResize = () => setWindowWidth(window.innerWidth);
  window.addEventListener("resize", handleResize);

  // spinner delay
  const spinnerTimer = setTimeout(() => {
    setShowSpinner(true);
  }, 400); // only show spinner if load takes longer than 400ms

  // simulate loading assets
  const loadPage = async () => {
    try {
      // example: fetch something or wait for assets
      await new Promise((resolve) => setTimeout(resolve, 200)); 
    } finally {
      clearTimeout(spinnerTimer);
      setIsPageReady(true);
    }
  };

  loadPage();

  return () => {
    window.removeEventListener("resize", handleResize);
    clearTimeout(spinnerTimer);
  };
}, []);

  const isMobile = windowWidth <= 700;
  const isTablet = windowWidth > 700 && windowWidth <= 1024;
  const isSmallScreen = windowWidth <= 1024;

  const countryOptions = [
    { value: "Sri Lanka", label: "Sri Lanka", flag: "https://flagcdn.com/lk.svg" },
    { value: "India", label: "India", flag: "https://flagcdn.com/in.svg" },
    { value: "USA", label: "USA", flag: "https://flagcdn.com/us.svg" },
    { value: "UK", label: "UK", flag: "https://flagcdn.com/gb.svg" },
    { value: "Canada", label: "Canada", flag: "https://flagcdn.com/ca.svg" },
    { value: "Australia", label: "Australia", flag: "https://flagcdn.com/au.svg" },
  ];

  const formatOptionLabel = ({ flag, label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <img
        src={flag}
        alt={label}
        style={{ width: "20px", borderRadius: "2px" }}
      />
      <span style={{ fontSize: "14px" }}>{label}</span>
    </div>
  );

   useEffect(() => {
    fetch("https://restcountries.com/v3.1/all?fields=name,flags")
      .then((res) => res.json())
      .then((data) => {
        const formatted = data.map((c) => ({
          value: c.name.common,
          label: (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img src={c.flags.svg} alt="flag" style={{ width: "20px", borderRadius: "2px" }} />
              {c.name.common}
            </div>
          ),
        })).sort((a, b) => a.value.localeCompare(b.value));
        setCountries(formatted);
      })
      .catch(() => toast.error("Failed to load countries"));
  }, []);

  // --- Registration Logic ---
  const handleUsernameChange = (e) => {
    setName(e.target.value.replace(/\s/g, ""));
  };

    const handleNewGoogleUser = (user) => {
    setTempUser(user);
    setShowDetailsPopup(true);
  };

   const handleSaveDetails = async (e) => {
      e.preventDefault();
      if (!additionalData.country || !additionalData.domainName) {
        toast.error("Please select your country and domain.");
        return;
      }
      const saveToast = toast.loading("Creating your profile...");
      try {
        await setDoc(doc(db, "users", tempUser.uid), {
          name: tempUser.displayName,
          email: tempUser.email,
          country: additionalData.country.value,
          domainName: additionalData.domainName,
          role: "user",
          createdAt: new Date()
        });
        toast.success("All set! Welcome.", { id: saveToast });
        setShowDetailsPopup(false);
        navigate("/userDashboard");
      } catch (err) {
        toast.error("Error saving data.", { id: saveToast });
      }
    };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    if (isFormInvalid) return;

    if (password.length < 6) {
      return toast.error("Password must be at least 6 characters.");
    }

    setLoading(true);
    recaptchaRef.current.execute();
  };

  const onCaptchaChange = async (token) => {
    if (!token) return;
    const toastId = toast.loading("Creating your account...");
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      await updateProfile(user, { displayName: name });

      await setDoc(doc(db, "users", user.uid), {
        name: name,
        email: email,
        country: country.value,
        role: "user",
        status: "in progress",
        createdAt: new Date(),
      });

      toast.success("Registration successful!", { id: toastId });
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const msg =
        err.code === "auth/email-already-in-use"
          ? "Email already in use."
          : err.message;
      toast.error(msg, { id: toastId });
      recaptchaRef.current.reset();
    }
    setLoading(false);
  };

  // --- Styles ---
  const pageWrapperStyle = {
    display: "flex",
    flexDirection: isSmallScreen ? "column" : "row",
    minHeight: "100vh",
    width: "100%",
    fontFamily: "'Inter', sans-serif",
    backgroundColor: isSmallScreen ? "#F3F4F6" : "white",
    overflowX: "hidden",
  };

  const loaderContainerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    width: "100%",
    backgroundColor: "#F3F4F6",
  };

  const spinnerStyle = {
    width: "50px",
    height: "50px",
    border: `5px solid rgba(255, 107, 0, 0.1)`,
    borderTop: `5px solid ${primaryOrange}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  };

  const leftSideStyle = {
    display: isSmallScreen ? "none" : "flex",
    flex: "1.4",
    position: "relative",
    backgroundImage: `url('https://images.unsplash.com/photo-1497215728101-856f4ea42174?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    flexDirection: "column",
    justifyContent: "center",
    padding: "80px",
    color: "white",
  };

  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(135deg, rgba(255, 107, 0, 0.9) 0%, rgba(15, 15, 15, 0.8) 100%)",
    zIndex: 1,
  };

  const backHomeButtonStyle = {
    position: "absolute",
    top: "40px",
    left: "40px",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "white",
    fontSize: "14px",
    fontWeight: "600",
    padding: "10px 20px",
    borderRadius: "30px",
    backgroundColor: isBackHovered
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    transition: "all 0.3s ease",
    cursor: "pointer",
  };

  const trustBadgeStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "13px",
    width: "fit-content",
    marginBottom: "20px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(4px)",
  };

  const rightSideStyle = {
    flex: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backgroundColor: isSmallScreen ? "#F3F4F6" : "white",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: isTablet ? "550px" : isMobile ? "450px" : "400px",
    backgroundColor: isSmallScreen ? "white" : "transparent",
    padding: isSmallScreen ? (isMobile ? "35px 25px" : "50px") : "0px",
    borderRadius: isSmallScreen ? "24px" : "0px",
    boxShadow: isSmallScreen ? "0 25px 50px -12px rgba(0, 0, 0, 0.08)" : "none",
    border: isSmallScreen ? "1px solid #E5E7EB" : "none",
    transition: "all 0.3s ease",
  };

  const getInputStyle = (id) => ({
    width: "100%",
    padding: "15px",
    margin: "6px 0",
    border: "none",
    borderBottom: `2px solid ${focusedInput === id ? primaryOrange : borderColor}`,
    outline: "none",
    fontSize: "15px",
    boxSizing: "border-box",
    transition: "all 0.3s ease",
    backgroundColor:
      focusedInput === id ? "rgba(255, 107, 0, 0.02)" : "transparent",
  });

  const getLabelStyle = (id) => ({
    fontSize: "11px",
    fontWeight: "700",
    color: focusedInput === id ? primaryOrange : "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    transition: "color 0.3s ease",
  });

  const selectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      borderBottom: `2px solid ${focusedInput === "country" ? primaryOrange : borderColor}`,
      borderRadius: 0,
      boxShadow: "none",
      margin: "7px 0",
      backgroundColor: "transparent",
      "&:hover": { borderColor: primaryOrange },
    }),
    valueContainer: (base) => ({ ...base, padding: "5px 0" }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  // --- Initial Loading Screen ---
if (!isPageReady && showSpinner) {
    return (
      <div style={loaderContainerStyle}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={spinnerStyle}></div>
      </div>
    );
  }

  return (
    <div style={pageWrapperStyle}>
      {/* Toast configuration added here to center text */}
      <Toaster 
        position="top-center" 
        toastOptions={{
            style: {
                textAlign: 'center',
                fontWeight: '500',
                fontSize: '14px',
                borderRadius: '10px',
            }
        }}
      />

      {/* --- Left Side Content --- */}
      {!isSmallScreen && (
        <div style={leftSideStyle}>
          <div style={overlayStyle}></div>
          <div
            style={backHomeButtonStyle}
            onMouseEnter={() => setIsBackHovered(true)}
            onMouseLeave={() => setIsBackHovered(false)}
            onClick={() => navigate("/")}
          >
            <ChevronLeft size={18} /> Back to Home
          </div>

          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={trustBadgeStyle}>
              <ShieldCheck size={18} color="#FFE0B2" />
              <span>
                Powering <strong style={{ color: "#FFE0B2" }}>10,000+</strong>{" "}
                Businesses Globally
              </span>
            </div>

            <h1
              style={{
                fontSize: "56px",
                fontWeight: "800",
                marginBottom: "24px",
                lineHeight: "1.1",
                letterSpacing: "-1.5px",
              }}
            >
              Start Your <br />
              <span style={{ color: "#FFE0B2" }}>Success</span> Story.
            </h1>
            <p
              style={{
                fontSize: "19px",
                opacity: 0.9,
                lineHeight: "1.6",
                maxWidth: "500px",
                fontWeight: "300",
              }}
            >
              Build your digital presence in minutes. The tools you need to
              scale are just one step away.
            </p>

            <div
              style={{
                marginTop: "50px",
                display: "flex",
                flexWrap: "wrap",
                gap: "40px",
              }}
            >
              <div style={{ display: "flex", gap: "15px", opacity: 0.9 }}>
                <Globe size={32} style={{ color: "#FFE0B2" }} />
                <div style={{ fontSize: "14px", lineHeight: "1.4" }}>
                  <strong style={{ display: "block", fontSize: "16px" }}>
                    Global Infrastructure
                  </strong>
                  Ready for any market.
                </div>
              </div>

              <div style={{ display: "flex", gap: "15px", opacity: 0.9 }}>
                <Lock size={32} style={{ color: "#FFE0B2" }} />
                <div style={{ fontSize: "14px", lineHeight: "1.4" }}>
                  <strong style={{ display: "block", fontSize: "16px" }}>
                    Advanced Security
                  </strong>
                  Enterprise-grade protection.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Right Side (Form) --- */}
      <div style={rightSideStyle}>
        <div style={cardStyle}>
          {isSmallScreen && (
            <div
              onClick={() => navigate("/")}
              style={{
                color: primaryOrange,
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              <ChevronLeft size={16} /> Home
            </div>
          )}

          <div
            style={{
              textAlign: isSmallScreen ? "left" : "center",
              marginBottom: "30px",
            }}
          >
            <h2
              style={{
                margin: "0 0 2px 0",
                fontSize: isMobile ? "28px" : "36px",
                fontWeight: "900",
                fontFamily: "'Poppins', sans-serif",
                background: `linear-gradient(to right, #111827, ${primaryOrange})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-1px",
              }}
            >
              Create Account
            </h2>
            <p style={{ color: "#6B7280", fontSize: "15px" }}>
              Join us and start your journey
            </p>
          </div>

          <form
            onSubmit={handleRegister}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div style={{ position: "relative" }}>
              <label style={getLabelStyle("name")}>User Domain</label>
              <input
                type="text"
                placeholder="e.g. asctzz"
                value={name}
                onFocus={() => setFocusedInput("name")}
                onBlur={() => setFocusedInput(null)}
                onChange={handleUsernameChange}
                style={getInputStyle("name")}
                required
              />
              {name && name.length < 3 && (
                <span style={{ color: "red", fontSize: "11px", display: "block", marginTop: "3px" }}>
                  Enter valid domain (minimum 3 characters)
                </span>
              )}
            </div>

            <div style={{ marginTop: "9px" }}>
              <label style={getLabelStyle("email")}>Email Address</label>
              <input
                type="email"
                placeholder="name@company.com"
                value={email}
                onFocus={() => setFocusedInput("email")}
                onBlur={() => setFocusedInput(null)}
                onChange={(e) => setEmail(e.target.value)}
                style={getInputStyle("email")}
                required
              />
            </div>

            <div style={{ marginTop: "10px" }}>
              <label style={getLabelStyle("country")}>Country</label>
              <Select
                options={countryOptions}
                value={country}
                onChange={setCountry}
                onFocus={() => setFocusedInput("country")}
                onBlur={() => setFocusedInput(null)}
                placeholder="Select your region"
                styles={selectStyles}
                formatOptionLabel={formatOptionLabel}
              />
            </div>

            <div style={{ position: "relative", marginTop: "9px" }}>
              <label style={getLabelStyle("pass")}>Password</label>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onFocus={() => setFocusedInput("pass")}
                onBlur={() => setFocusedInput(null)}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...getInputStyle("pass"), paddingRight: "45px" }}
                required
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "35px",
                  cursor: "pointer",
                  color: "#9CA3AF",
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                margin: "16px 0",
              }}
            >
              <input
                type="checkbox"
                id="agree-checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                style={{
                  accentColor: primaryOrange,
                  width: "16px",
                  height: "16px",
                }}
              />
              <label htmlFor="agree-checkbox" style={{ fontSize: "12px", color: "#6B7280", cursor: "pointer" }}>
                I agree to the{" "}
                <Link
                  to="/terms"
                  style={{
                    color: primaryOrange,
                    fontWeight: "600",
                    textDecoration: "none",
                  }}
                >
                  Terms & Conditions
                </Link>
              </label>
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey="6Ld46YMsAAAAAEBokkvD6jYZ9yBZod1-DEqX7PN-"
              onChange={onCaptchaChange}
            />

            <button
              type="submit"
              disabled={isFormInvalid}
              style={{
                width: "100%",
                padding: "18px",
                background: isFormInvalid 
                  ? "#D1D5DB" 
                  : `linear-gradient(135deg, ${primaryOrange} 0%, ${darkOrange} 100%)`,
                color: "white",
                border: "none",
                borderRadius: "14px",
                cursor: isFormInvalid ? "not-allowed" : "pointer",
                fontWeight: "700",
                fontSize: "16px",
                boxShadow: isFormInvalid ? "none" : "0 10px 15px -3px rgba(255, 107, 0, 0.3)",
                transition: "all 0.2s ease",
                opacity: isFormInvalid ? 0.7 : 1,
              }}
            >
              {loading ? "Creating Account..." : "REGISTER NOW"}
            </button>
          </form>

          <div style={{ display: "flex", alignItems: "center", margin: "30px 0", color: "#E5E7EB" }}>
                      <div style={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
                      <span style={{ margin: "0 15px", fontSize: "11px", color: "#9CA3AF", fontWeight: "700", letterSpacing: "1px" }}>OR</span>
                      <div style={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
                    </div>
          
          <GoogleSignInButton onNewUserFound={handleNewGoogleUser} />

          <div
            style={{
              marginTop: "25px",
              fontSize: "14px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            Already have an account?{" "}
            <Link
              to="/login"
              style={{
                color: primaryOrange,
                fontWeight: "700",
                textDecoration: "none",
              }}
            >
              Login Now
            </Link>
          </div>
        </div>
      </div>

         {showDetailsPopup && (
              <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, backdropFilter: "blur(10px)" }}>
                <div style={{ backgroundColor: "white", padding: "40px", borderRadius: "24px", width: "90%", maxWidth: "420px", textAlign: "center" }}>
                  <h3 style={{ color: "#111827", fontSize: "24px", fontWeight: "800", marginBottom: "10px" }}>Complete Profile</h3>
                  <form onSubmit={handleSaveDetails}>
                    <div style={{ textAlign: "left", marginBottom: "20px" }}>
                      <label style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "8px", display: "block" }}>Select Country</label>
                      <Select options={countries} onChange={(opt) => setAdditionalData({ ...additionalData, country: opt })} />
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <label style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "5px", display: "block" }}>Business Domain</label>
                      <input style={getInputStyle('domain')} placeholder="e.g. cloudscape.io" required value={additionalData.domainName} onChange={(e) => setAdditionalData({ ...additionalData, domainName: e.target.value.replace(/\s/g, "") })} />
                    </div>
                    <button type="submit" style={{ width: "100%", padding: "16px", marginTop: "30px", background: primaryOrange, color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: "pointer" }}>
                      FINISH & EXPLORE
                    </button>
                  </form>
                </div>
              </div>
            )}
    </div>
  );
};

export default RegisterForm;